import { Link } from 'react-router-dom';
import { trackEvent } from '../lib/analytics';

/**
 * ApplyLink
 * ─────────
 * Every "Apply Now" CTA across the site renders through this component.
 *
 * Behaviour is driven by REACT_APP_APPLY_URL:
 *
 *   - unset (or empty)  → renders an internal <Link to="/apply">
 *                         (keeps dev + current prod behaviour working)
 *
 *   - starts with http  → renders an external <a href target="_blank">
 *                         opens the vendor form / partner portal in a new tab,
 *                         with rel="noopener noreferrer" so the external page
 *                         can't touch window.opener or leak referrer data
 *
 *   - anything else     → treated as an internal path ("/some-path") and
 *                         rendered as an internal <Link>
 *
 * When the business supplies a real URL, just set
 *   REACT_APP_APPLY_URL=https://<the-real-form-url>
 * in the website's prod env and redeploy — no code changes needed.
 */
const APPLY_URL = process.env.REACT_APP_APPLY_URL || '/apply';
const IS_EXTERNAL = /^https?:\/\//i.test(APPLY_URL);

export default function ApplyLink({
  className,
  children,
  onClick,
  'aria-label': ariaLabel,
  /** Optional string identifying where on the page the click happened
   *  (e.g. "hero", "navbar", "sticky_bar") — recorded as the `location`
   *  parameter of the GA4 `apply_click` event for funnel attribution. */
  location: ctaLocation,
}) {
  const handleClick = (e) => {
    // Two events for the same click:
    //   1. apply_click — internal taxonomy with destination + page location
    //      for funnel attribution (which CTA on which page converts best).
    //   2. generate_lead — GA4's recommended conversion event. Apply clicks
    //      are the highest-intent action on the site; the actual submission
    //      happens externally on Jotform so the click is our best proxy.
    //      Mark generate_lead as a conversion in GA4 Admin → Events to
    //      see it in Realtime + Acquisition reports.
    trackEvent('apply_click', {
      destination: IS_EXTERNAL ? 'jotform' : 'internal',
      location: ctaLocation || 'unknown',
    });
    trackEvent('generate_lead', {
      source: 'apply_button',
      location: ctaLocation || 'unknown',
      currency: 'GBP',
      value: 1,
    });
    if (onClick) onClick(e);
  };

  if (IS_EXTERNAL) {
    return (
      <a
        href={APPLY_URL}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        aria-label={ariaLabel}
      >
        {children}
      </a>
    );
  }
  return (
    <Link
      to={APPLY_URL}
      className={className}
      onClick={handleClick}
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
}
